/**
 * Authentication Context for Admin Panel
 * Reuses the authentication logic from the main web app
 * This ensures consistency across applications
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { tokenManager } from '../utils/tokenManager';
import { LoginRequest } from '../types/api';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'master' | 'risk' | 'finance' | 'support' | 'compliance' | 'content';
  active: boolean;
  last_login_at?: string | null;
}

interface AuthContextType {
  admin: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (request: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Set up unauthorized callback for API service
  useEffect(() => {
    apiService.setUnauthorizedCallback(() => {
      console.log('Unauthorized (401) detected, logging out user...');
      logout();
      navigate('/login');
    });

    // Set up suspended callback for API service
    apiService.setSuspendedCallback(() => {
      console.log('Account suspended (403) detected, logging out user...');
      // Set admin active to false to trigger SuspendedModal
      setAdmin((prev) => prev ? { ...prev, active: false } : null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const authenticated = tokenManager.isAuthenticated();
    if (authenticated) {
      // Check if we're on first login page - if so, don't set authenticated state yet
      const currentPath = window.location.pathname;
      if (currentPath === '/first-login') {
        // On first login page, don't auto-authenticate - let FirstLoginSetupPage handle it
        return;
      }
      setIsAuthenticated(authenticated);
      loadUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect to dashboard when authenticated (but not if on first-login page)
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const currentPath = window.location.pathname;
      // Don't redirect if already on first-login page
      if (currentPath === '/first-login') {
        return;
      }
      // Check if first login is required before redirecting to dashboard
      const checkAndRedirect = async () => {
        try {
          const firstLoginStatus = await apiService.checkFirstLoginStatus();
          if (firstLoginStatus.requiresFirstLogin) {
            // First login required, go to first login page
            navigate('/first-login');
          } else {
            // First login complete, go to dashboard
            navigate('/dashboard');
          }
        } catch (err) {
          // If check fails, redirect to dashboard anyway
          console.error('Failed to check first login status:', err);
          navigate('/dashboard');
        }
      };
      checkAndRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  const login = async (request: LoginRequest) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.adminLogin(request.email, request.password);
      tokenManager.saveTokens(
        response.token,
        response.refreshToken,
        response.admin.email
      );
      setAdmin({
        id: response.admin.id,
        email: response.admin.email,
        name: response.admin.name,
        role: response.admin.role as AdminUser['role'],
        active: response.admin.active ?? true,
      });
      setIsAuthenticated(true);
      setIsLoading(false);
    } catch (err) {
      let errorMessage = 'Login failed';
      if (err instanceof Error) {
        errorMessage = err.message;
        // Check for suspended account error
        if (errorMessage.includes('suspended') || errorMessage.includes('Account Suspended') || errorMessage.includes('deactivated')) {
          errorMessage = 'Your account has been suspended';
        }
      }
      setError(errorMessage);
      setIsLoading(false);
      throw err;
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      const refreshToken = tokenManager.getRefreshToken();
      if (refreshToken) {
        try {
          await apiService.adminLogout(refreshToken);
        } catch (err) {
          console.error('Logout API call failed:', err);
        }
      }
      tokenManager.clearTokens();
      // Clear admin panel state from localStorage on logout
      localStorage.removeItem('admin_current_panel');
      localStorage.removeItem('admin_current_page');
      localStorage.removeItem('admin_sidebar_open');
      // Clear sessionStorage flags
      sessionStorage.removeItem('first_login_just_completed');
      setAdmin(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      navigate('/login');
    } catch (err) {
      tokenManager.clearTokens();
      // Clear admin panel state from localStorage on logout
      localStorage.removeItem('admin_current_panel');
      localStorage.removeItem('admin_current_page');
      localStorage.removeItem('admin_sidebar_open');
      // Clear sessionStorage flags
      sessionStorage.removeItem('first_login_just_completed');
      setAdmin(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      navigate('/login');
    }
  };

  const loadUser = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getCurrentAdmin();
      // Check if account is disabled
      if (response.admin.active === false) {
        setAdmin({
          id: response.admin.id,
          email: response.admin.email,
          name: response.admin.name,
          role: response.admin.role as AdminUser['role'],
          active: false,
          last_login_at: response.admin.last_login_at,
        });
        setIsLoading(false);
        return;
      }
      
      // Check if first login is required
      // Skip this check if we just completed first login (race condition prevention)
      const justCompleted = sessionStorage.getItem('first_login_just_completed') === 'true';
      if (!justCompleted) {
        try {
          const firstLoginStatus = await apiService.checkFirstLoginStatus();
          if (firstLoginStatus.requiresFirstLogin) {
            // Redirect to first login setup
            navigate('/first-login');
            setIsLoading(false);
            return;
          }
        } catch (err) {
          // If check fails, continue with normal flow
          console.error('Failed to check first login status:', err);
        }
      }
      
      setAdmin({
        id: response.admin.id,
        email: response.admin.email,
        name: response.admin.name,
        role: response.admin.role as AdminUser['role'],
        active: response.admin.active,
        last_login_at: response.admin.last_login_at,
      });
      setIsAuthenticated(true);
      setIsLoading(false);
    } catch (err) {
      if (err instanceof Error) {
        // Check for suspended account error
        if (err.message.includes('suspended') || err.message.includes('Account Suspended') || err.message.includes('deactivated')) {
          // Set admin to suspended state to trigger SuspendedModal
          setAdmin((prev) => prev ? { ...prev, active: false } : null);
          setIsLoading(false);
          return;
        }
        if (err.message.includes('401')) {
          console.log('Admin unauthorized (401), logging out...');
          await logout();
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to load admin user');
      }
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        admin,
        isAuthenticated,
        isLoading,
        error,
        login,
        logout,
        loadUser,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

